/**
 * Autenticação — consome os endpoints REST de sessão do back-end existente:
 *   POST /api/auth/login   { email, senha }       -> { status, ...sessao } | 401 { status:false, erro }
 *   POST /api/auth/logout                          -> { sucesso: true }
 *   GET  /api/auth/me                              -> { authenticated, user }
 *   POST /api/auth/select-organization { codOrganizacao }  (apenas suporte)
 */
import type { UsuarioSessao } from "../types/auth";

/** Lançado quando o login exige o código do autenticador (2FA ativo para a conta). */
export class Requer2FAError extends Error {
  constructor() {
    super("2fa_obrigatorio");
    this.name = "Requer2FAError";
  }
}

/** O servidor já verificou as credenciais, mas aguarda a troca de dispositivo. */
export class SessaoEmUsoError extends Error {
  constructor(public dispositivoAnterior: string, public dispositivoAtual: string) {
    super("Este login já está em uso em outro dispositivo.");
    this.name = "SessaoEmUsoError";
  }
}

function verificarConflitoSessao(data: { erro?: string; dispositivoAnterior?: string; dispositivoAtual?: string } | null) {
  if (data?.erro === "sessao_em_uso") {
    throw new SessaoEmUsoError(data.dispositivoAnterior || "Outro dispositivo", data.dispositivoAtual || "Este dispositivo");
  }
}

/** Situação do widget anti-bot (Cloudflare Turnstile) exigido na tela de login. */
export async function obterTurnstileLogin(): Promise<{ exigido: boolean; siteKey: string }> {
  try {
    const res = await fetch("/api/auth/turnstile", { credentials: "include", cache: "no-store" });
    const data = await safeJson(res);
    return { exigido: Boolean(data?.exigido), siteKey: String(data?.siteKey || "") };
  } catch {
    return { exigido: false, siteKey: "" };
  }
}

export async function login(
  email: string,
  senha: string,
  turnstileToken = "",
  codEmpresa = ""
): Promise<UsuarioSessao> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify({ email, senha, turnstileToken, codEmpresa }),
  });

  const data = await safeJson(res);

  if (!res.ok || !data?.status) {
    verificarConflitoSessao(data);
    const erro = data?.erro;
    if (erro === "2fa_obrigatorio") throw new Requer2FAError();
    if (erro === "credenciais") throw new Error("E-mail ou senha inválidos.");
    if (erro === "turnstile") {
      throw new Error(
        typeof data?.mensagem === "string" && data.mensagem
          ? data.mensagem
          : "Não foi possível concluir a verificação anti-bot."
      );
    }
    if (erro === "email") throw new Error("E-mail não encontrado.");
    if (erro === "senha") throw new Error("Senha incorreta.");
    if (erro === "empresa_sem_acesso") throw new Error("Este login não possui acesso à empresa selecionada.");
    if (erro === "login_inativo") throw new Error(mensagemOu(data, "Login inativo. Fale com um administrador."));
    if (erro === "perfil_inativo") {
      throw new Error(mensagemOu(data, "Perfil de acesso inativo. Fale com um administrador."));
    }
    if (erro === "inativo") throw new Error("Usuário ou perfil inativo. Fale com um administrador.");
    if (erro === "acesso_web_bloqueado") {
      throw new Error("Usuário sem acesso à plataforma web. Fale com um administrador.");
    }
    if (erro === "horario") {
      throw new Error(
        typeof data?.mensagem === "string" && data.mensagem
          ? data.mensagem
          : "Fora do horário de acesso permitido para o seu perfil."
      );
    }
    throw new Error(typeof erro === "string" && erro ? erro : "Não foi possível entrar.");
  }

  return data as UsuarioSessao;
}

export async function loginComGoogle(accessToken: string, codEmpresa = ""): Promise<UsuarioSessao> {
  const res = await fetch("/api/auth/login-google", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify({ accessToken, codEmpresa }),
  });

  const data = await safeJson(res);

  if (!res.ok || !data?.status) {
    verificarConflitoSessao(data);
    const erro = data?.erro;
    if (erro === "2fa_obrigatorio") throw new Requer2FAError();
    if (erro === "email_nao_cadastrado") throw new Error("Este e-mail não está cadastrado.");
    if (erro === "empresa_sem_acesso") throw new Error("Este login não possui acesso à empresa selecionada.");
    if (erro === "login_inativo") throw new Error(mensagemOu(data, "Login inativo. Fale com um administrador."));
    if (erro === "perfil_inativo") {
      throw new Error(mensagemOu(data, "Perfil de acesso inativo. Fale com um administrador."));
    }
    if (erro === "inativo") throw new Error("Usuário ou perfil inativo. Fale com um administrador.");
    if (erro === "acesso_web_bloqueado") {
      throw new Error("Usuário sem acesso à plataforma web. Fale com um administrador.");
    }
    if (erro === "horario") {
      throw new Error(
        typeof data?.mensagem === "string" && data.mensagem
          ? data.mensagem
          : "Fora do horário de acesso permitido para o seu perfil."
      );
    }
    if (erro === "google_nao_configurado") throw new Error("Login com Google não está configurado.");
    throw new Error("Não foi possível entrar com o Google.");
  }

  return data as UsuarioSessao;
}

export async function verificarCodigo2FA(codigo: string): Promise<UsuarioSessao> {
  const res = await fetch("/api/auth/verificar-2fa", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify({ codigo }),
  });

  const data = await safeJson(res);

  if (!res.ok || !data?.status) {
    verificarConflitoSessao(data);
    const erro = data?.erro;
    if (erro === "codigo_invalido") throw new Error("Código inválido.");
    if (erro === "login_inativo") throw new Error(mensagemOu(data, "Login inativo. Fale com um administrador."));
    if (erro === "perfil_inativo") {
      throw new Error(mensagemOu(data, "Perfil de acesso inativo. Fale com um administrador."));
    }
    if (erro === "sessao_expirada") throw new Error("Sessão de verificação expirada. Faça login novamente.");
    if (erro === "empresa_sem_acesso") throw new Error("A empresa selecionada não está mais disponível para este login.");
    throw new Error("Não foi possível verificar o código.");
  }

  return data as UsuarioSessao;
}

/** Envia por e-mail um código alternativo ao do aplicativo autenticador (login pendente de 2FA). */
export async function solicitarCodigo2FAPorEmail(): Promise<void> {
  const res = await fetch("/api/auth/2fa-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
  });

  const data = await safeJson(res);

  if (!res.ok || !data?.status) {
    const erro = data?.erro;
    if (erro === "sessao_expirada") throw new Error("Sessão de verificação expirada. Faça login novamente.");
    if (erro === "envio_falhou") throw new Error("Não foi possível enviar o código por e-mail.");
    throw new Error("Não foi possível enviar o código por e-mail.");
  }
}

export type EscopoAutorizacaoAdmin = "vendas_editar" | "vendas_excluir" | "financeiro_excluir";

export async function verificarAdmin(email: string, senha: string, escopo?: EscopoAutorizacaoAdmin): Promise<void> {
  const res = await fetch("/api/auth/verify-admin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify({ email, senha, escopo }),
  });

  const data = await safeJson(res);

  if (!res.ok || !data?.autorizado) {
    const erro = data?.erro;
    if (erro === "credenciais") throw new Error("E-mail ou senha inválidos.");
    if (erro === "email") throw new Error("E-mail não encontrado.");
    if (erro === "senha") throw new Error("Senha incorreta.");
    if (erro === "organizacao") throw new Error("Usuário não pertence a esta organização.");
    if (erro === "nivel") throw new Error("Usuário não é Administrador da Organização ou da Empresa.");
    throw new Error("Não foi possível autorizar.");
  }
}

export async function confirmarSessaoAqui(): Promise<UsuarioSessao> {
  const res = await fetch("/api/auth/confirmar-sessao", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
    body: "{}",
  });
  const data = await safeJson(res);
  if (!res.ok || !data?.status) throw new Error(mensagemOu(data, "Não foi possível confirmar. Faça login novamente."));
  return data as UsuarioSessao;
}

export async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    /* logout é best-effort; segue limpando o estado local */
  }
}

/** Resultado de `/api/auth/me`: a sessão e, quando ela foi recusada, o motivo. */
export interface ResultadoSessao {
  usuario: UsuarioSessao | null;
  /** Ex.: "login_inativo" quando o login foi desativado com a sessão aberta. */
  erro?: string;
  /** Texto pronto para a tela de login exibir ao usuário. */
  mensagem?: string;
  indisponivel?: boolean;
  configuracoesSessao?: { sessaoUnica: boolean; logoutInatividade: boolean; logoutInatividadeMinutos: number };
}

export async function obterSessaoDetalhada(): Promise<ResultadoSessao> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const res = await fetch("/api/auth/me", { credentials: "include", cache: "no-store", signal: controller.signal });
    if (res.status >= 500) return { usuario: null, indisponivel: true };
    const data = await safeJson(res);
    if (data?.authenticated) return {
      usuario: data.user as UsuarioSessao,
      configuracoesSessao: data.configuracoesSessao,
    };
    return {
      usuario: null,
      erro: typeof data?.erro === "string" ? data.erro : undefined,
      mensagem: typeof data?.mensagem === "string" ? data.mensagem : undefined,
    };
  } catch {
    return { usuario: null, indisponivel: true };
  } finally {
    window.clearTimeout(timer);
  }
}

export async function obterSessao(): Promise<UsuarioSessao | null> {
  return (await obterSessaoDetalhada()).usuario;
}

export async function selecionarOrganizacao(codOrganizacao: number | string): Promise<void> {
  const res = await fetch("/api/auth/select-organization", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ codOrganizacao }),
  });
  const data = await safeJson(res);
  if (!res.ok || !data?.sucesso) {
    throw new Error(data?.erro || "Não foi possível trocar a organização.");
  }
}

export async function selecionarEmpresa(codEmpresa: number | string): Promise<void> {
  const res = await fetch("/api/auth/select-company", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ codEmpresa }),
  });
  const data = await safeJson(res);
  if (!res.ok || !data?.sucesso) {
    throw new Error(data?.erro || "Não foi possível trocar a empresa.");
  }
}

/** Usa a mensagem que veio do back-end; cai no texto padrão quando não veio nenhuma. */
function mensagemOu(data: any, padrao: string): string {
  return typeof data?.mensagem === "string" && data.mensagem ? data.mensagem : padrao;
}

async function safeJson(res: Response): Promise<any> {
  const raw = await res.text();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
